import type { MutationResult } from '@/graphql/types';

/**
 * GraphQL Types for Task Manager
 *
 * This file contains TypeScript interfaces and types for GraphQL operations
 * related to task management, including tasks and sections.
 */

export enum TaskPriority {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

export const priorityStyle: Record<TaskPriority, string> = {
  [TaskPriority.HIGH]: 'bg-error-background text-error border-error',
  [TaskPriority.MEDIUM]: 'bg-warning-background text-warning border-warning',
  [TaskPriority.LOW]: 'bg-secondary-50 text-secondary border-secondary',
};

export interface ItemTag {
  ItemId: string;
  TagLabel: string;
}

export interface Assignee {
  ItemId: string;
  Name: string;
  ImageUrl: string;
}

export interface TaskComments {
  ItemId: string;
  TaskId?: string;
  Content: string;
  Timestamp: string;
  Author: string;
  IsDeleted?: boolean;
  Tags?: string[];
  Language?: string;
  OrganizationIds?: string[];
  CreatedBy?: string;
  CreatedDate?: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
}

export type FileType = 'pdf' | 'image' | 'other';

export interface TaskAttachments {
  ItemId: string;
  FileName: string;
  FileSize: string;
  FileType: FileType;
  FileUrl: string;
}

export interface TaskItem {
  ItemId: string;
  Title: string;
  CreatedBy?: string;
  CreatedDate?: string;
  IsDeleted?: boolean;
  IsCompleted: boolean;
  Language?: string;
  Description?: string;
  Assignee?: Assignee[];
  DueDate?: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
  OrganizationIds?: string[];
  Priority?: TaskPriority;
  Section?: string;
  Tags?: string[];
  ItemTag?: ItemTag[];
  ItemTags?: string[];
  AttachmentField?: TaskAttachments[];
}

export interface TaskSection {
  ItemId: string;
  Title: string;
  CreatedBy: string;
  CreatedDate: string;
  IsDeleted: boolean;
  Language: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
  OrganizationIds: string[];
  Tags?: string[];
  tasks?: TaskItem[];
}

export interface TaskTag {
  ItemId: string;
  Label: string;
  CreatedBy: string;
  CreatedDate: string;
  IsDeleted: boolean;
  Language: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
  OrganizationIds: string[];
  Tags?: string[];
}

export interface TaskSectionWithTasks extends TaskSection {
  tasks: TaskItem[];
}

export interface GetTasksResponse {
  TaskManagerItems: {
    items: TaskItem[];
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    pageNo: number;
    totalPages: number;
  };
}

export interface GetSectionsResponse {
  TaskManagerSections: {
    items: TaskSection[];
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    pageNo: number;
    totalPages: number;
  };
}

export interface GetTagsResponse {
  TaskManagerTags: {
    items: TaskTag[];
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    pageNo: number;
    totalPages: number;
  };
}

export interface GetCommentsResponse {
  TaskManagerComments: {
    items: TaskComments[];
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageSize: number;
    pageNo: number;
    totalPages: number;
  };
}

export interface TaskItemInsertInput {
  Title: string;
  Description?: string;
  Assignee?: Assignee[];
  DueDate?: string;
  Priority?: TaskPriority;
  Section?: string;
  Tags?: string[];
  ItemTag?: ItemTag[];
  IsCompleted?: boolean;
  OrganizationIds?: string[];
}

export interface TaskItemUpdateInput {
  Title?: string;
  Language?: string;
  IsDeleted?: boolean;
  Description?: string;
  Assignee?: Assignee[];
  ItemTag?: ItemTag[];
  AttachmentField?: TaskAttachments[];
  DueDate?: string;
  Priority?: TaskPriority;
  Section?: string;
  IsCompleted?: boolean;
  OrganizationIds?: string[];
  Tags?: string[];
}

export interface TaskTagInsertInput {
  Label: string;
  OrganizationIds?: string[];
  Tags?: string[];
}

export interface TaskTagUpdateInput extends Partial<TaskTagInsertInput> {
  IsDeleted?: boolean;
}

export interface TaskSectionInsertInput {
  ItemId?: string;
  Assignee?: string;
  DueDate?: string;
  Title: string;
  Language?: string;
  OrganizationIds?: string[];
  Tags?: string[];
}

export interface TaskSectionUpdateInput extends Partial<TaskSectionInsertInput> {
  IsDeleted?: boolean;
}

export type UpdateTaskManagerSectionResponse = UpdateTaskSectionResponse;

export type PaginationParams = import('@/graphql/types').ListQueryVariables<Record<string, unknown>>;

export interface TaskCommentInsertInput {
  TaskId: string;
  Content: string;
  Timestamp?: string;
  Author?: string;
  Tags?: string[];
  OrganizationIds?: string[];
}

export interface TaskCommentUpdateInput {
  Content?: string;
  Timestamp?: string;
  Author?: string;
}

/** Fields selected by the standalone attachment query. */
export interface TaskAttachmentRecord extends Omit<TaskAttachments, 'FileUrl'> {
  TaskId: string;
  CreatedBy?: string;
  CreatedDate?: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
}

export interface TaskAttachmentPage extends Omit<GetTasksResponse['TaskManagerItems'], 'items'> {
  items: TaskAttachmentRecord[];
}

export type BaseMutationResponse = MutationResult;

export interface InsertTaskItemResponse {
  insertTaskManagerItem: MutationResult & { itemId: string };
}

export interface UpdateTaskItemResponse {
  updateTaskManagerItem: MutationResult;
}

export interface DeleteTaskItemResponse {
  deleteTaskManagerItem: MutationResult;
}

export interface InsertTaskSectionResponse {
  insertTaskManagerSection: MutationResult;
}

export interface UpdateTaskSectionResponse {
  updateTaskManagerSection: MutationResult;
}

export interface DeleteTaskSectionResponse {
  deleteTaskManagerSection: MutationResult;
}

export interface InsertTaskTagResponse {
  insertTaskManagerTag: MutationResult & { itemId: string };
}

export interface UpdateTaskTagResponse {
  updateTaskManagerTag: MutationResult;
}

export interface DeleteTaskTagResponse {
  deleteTaskManagerTag: MutationResult;
}

export interface InsertTaskCommentResponse {
  insertTaskComment: MutationResult & { itemId: string };
}

export interface UpdateTaskCommentResponse {
  updateTaskComment: MutationResult;
}

export interface DeleteTaskCommentResponse {
  deleteTaskComment: MutationResult;
}

export interface InsertTaskAttachmentResponse {
  insertTaskAttachment: MutationResult;
}

export interface UpdateTaskAttachmentResponse {
  updateTaskAttachment: MutationResult;
}

export interface DeleteTaskAttachmentResponse {
  deleteTaskAttachmentItem: MutationResult;
}
